// {
//   id: 1,
//   author: "James Nash",
//   content: "Test",
//   timestamp: "01/12/2025 04:57 PM",
//   priority: "high",
//   resolved: false,
//   replies: [
//     {
//       id: 2,
//       author: "James Nash",
//       content: "reply",
//       timestamp: "01/12/2025 04:57 PM",
//       priority: "high",
//     },
//     {
//       id: 3,
//       author: "James Nash",
//       content: "reply 1",
//       timestamp: "01/12/2025 04:57 PM",
//       priority: "high",
//     },
//   ],
// },

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import "./commentSystem.scss";
import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { useUserData } from "../../../hooks/useUserData";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";

function normalizeStaffId(id) {
  return String(id || "")
    .trim()
    .toLowerCase()
    .replace(/^\{|\}$/g, "");
}

function getMentionLabel(staff) {
  return String(staff?.label || staff?.name || staff?.StaffName || "").trim();
}

function getMentionId(staff) {
  return String(staff?.value || staff?.id || staff?.StaffId || "").trim();
}

function getMentionEmail(staff) {
  return String(staff?.email || staff?.EmailId || "").trim();
}

function getInitials(name) {
  const parts = String(name || "U")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }
  return (parts[0]?.[0] || "U").toUpperCase();
}

function mapMentionCandidates(rawRows = []) {
  const seen = new Set();
  const rows = [];
  (rawRows || []).forEach((item) => {
    const id = String(item?.id || item?.StaffId || item?.value || "").trim();
    const label = String(item?.name || item?.label || item?.StaffName || "").trim();
    if (!id || !label) return;
    const key = id.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({
      value: id,
      label,
      email: String(item?.EmailId || item?.email || "").trim(),
      Role: item?.Role || item?.role || "",
      IsCRO: item?.IsCRO ?? item?.isCRO ?? 0,
    });
  });
  rows.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );
  return rows;
}

function buildStaffIdListFromComment(text, staffOptions) {
  const list = [];
  const seen = new Set();
  const body = String(text || "");
  (staffOptions || []).forEach((staff) => {
    const label = getMentionLabel(staff);
    const id = getMentionId(staff);
    if (!label || !id) return;
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`@${escaped}(?:\\s|$|[.,!?;:])`, "i");
    if (!pattern.test(body)) return;
    const key = id.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    list.push({
      StaffIdRef: id,
      StaffEmail: getMentionEmail(staff),
      StaffName: label,
    });
  });
  return list.length ? list : null;
}

function renderCommentBody(text) {
  const value = String(text || "");
  if (!value) return null;
  const segments = value.split(/(@[^\n@]+?)(?=\s|$|[.,!?;:])/g);
  return segments.map((segment, index) => {
    if (segment.startsWith("@")) {
      return (
        <span key={`mention-${index}`} className="comment-mention-inline">
          {segment}
        </span>
      );
    }
    return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
  });
}

function MentionTextarea({
  value,
  mentionIds,
  staffOptions,
  disabled,
  placeholder,
  onChange,
  onMentionIdsChange,
  onSubmit,
}) {
  const textareaRef = useRef(null);
  const menuRef = useRef(null);
  const [mentionMenu, setMentionMenu] = useState(null);

  const mentionCandidates = useMemo(() => {
    if (!mentionMenu) return [];
    const query = mentionMenu.query.trim().toLowerCase();
    const available = (staffOptions || []).filter((staff) => {
      const id = getMentionId(staff);
      return id && !mentionIds.includes(id);
    });
    if (!query) return available;
    return available.filter((staff) =>
      getMentionLabel(staff).toLowerCase().includes(query),
    );
  }, [mentionMenu, mentionIds, staffOptions]);

  const syncMentionMenu = (nextValue, cursorPosition) => {
    const textBeforeCursor = nextValue.slice(0, cursorPosition);
    const match = textBeforeCursor.match(/@([^\s@]*)$/);
    if (!match) {
      setMentionMenu(null);
      return;
    }
    setMentionMenu({
      start: cursorPosition - match[0].length,
      query: match[1] || "",
      activeIndex: 0,
    });
  };

  const insertMention = (staff) => {
    if (!staff || !mentionMenu || !textareaRef.current) return;
    const label = getMentionLabel(staff);
    const id = getMentionId(staff);
    const cursor = textareaRef.current.selectionStart;
    const before = value.slice(0, mentionMenu.start);
    const after = value.slice(cursor);
    const mentionText = `@${label} `;
    const nextValue = `${before}${mentionText}${after}`;
    const nextCursor = before.length + mentionText.length;
    onChange(nextValue);
    onMentionIdsChange((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setMentionMenu(null);
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleKeyDown = (event) => {
    if (mentionMenu && mentionCandidates.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionMenu((current) => ({
          ...current,
          activeIndex: Math.min(
            current.activeIndex + 1,
            mentionCandidates.length - 1,
          ),
        }));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionMenu((current) => ({
          ...current,
          activeIndex: Math.max(current.activeIndex - 1, 0),
        }));
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertMention(mentionCandidates[mentionMenu.activeIndex]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMentionMenu(null);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit?.();
    }
  };

  useEffect(() => {
    if (!mentionMenu) return undefined;
    const onMouseDown = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        textareaRef.current?.contains(event.target)
      ) {
        return;
      }
      setMentionMenu(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [mentionMenu]);

  return (
    <div className="comment-mention-wrap">
      {mentionMenu ? (
        <div ref={menuRef} className="comment-mention-menu" role="listbox">
          {staffOptions.length === 0 ? (
            <p className="comment-mention-empty">
              No study users available to tag.
            </p>
          ) : mentionCandidates.length === 0 ? (
            <p className="comment-mention-empty">
              No matches for @{mentionMenu.query}
            </p>
          ) : (
            mentionCandidates.map((staff, index) => {
              const label = getMentionLabel(staff);
              return (
                <button
                  key={getMentionId(staff)}
                  type="button"
                  role="option"
                  aria-selected={index === mentionMenu.activeIndex}
                  className={`comment-mention-option${
                    index === mentionMenu.activeIndex ? " is-active" : ""
                  }`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertMention(staff);
                  }}
                >
                  <span className="comment-mention-avatar">
                    {getInitials(label)}
                  </span>
                  <span className="comment-mention-label">{label}</span>
                  {staff.Role ? (
                    <span className="comment-mention-role">{staff.Role}</span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        className="form-control comment-mention-input"
        rows={2}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(nextValue);
          syncMentionMenu(nextValue, event.target.selectionStart);
        }}
        onClick={(event) =>
          syncMentionMenu(event.target.value, event.target.selectionStart)
        }
        onKeyUp={(event) =>
          syncMentionMenu(event.target.value, event.target.selectionStart)
        }
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

const CommentSystem = ({
  inputNode,
  studyId,
  isOpen,
  readOnly = false,
  staffList = [],
  templateCategory = "",
}) => {
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { userData } = useUserData();

  const currentStaffId = normalizeStaffId(userData?.StaffId);

  const isCommentAuthor = (comment) =>
    normalizeStaffId(comment?.authorStaffId) === currentStaffId;

  const canDeleteComment = (comment) =>
    !readOnly &&
    isCommentAuthor(comment) &&
    !(comment?.replies && comment.replies.length > 0);

  const canDeleteReply = (reply) =>
    !readOnly && normalizeStaffId(reply?.authorStaffId) === currentStaffId;

  const canParticipateInComments = () => !readOnly && Boolean(currentStaffId);

  const [activeTab, setActiveTab] = useState("unresolved");
  const [showOnlyHigh, setShowOnlyHigh] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [mentionIds, setMentionIds] = useState([]);
  const [mentionStaff, setMentionStaff] = useState([]);
  const [markAsHigh, setMarkAsHigh] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [scrollToId, setScrollToId] = useState(null);

  const commentRefs = useRef({});
  const commentsListRef = useRef(null);

  const [comments, setComments] = useState([]);

  const mentionOptions = useMemo(() => {
    return mapMentionCandidates([
      ...(staffList || []),
      ...mentionStaff,
    ]).filter((row) => normalizeStaffId(row.value) !== currentStaffId);
  }, [staffList, mentionStaff, currentStaffId]);

  const loadMentionCandidates = useCallback(async () => {
    if (!userData?.OrganizationId || !studyId) {
      setMentionStaff([]);
      return;
    }
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.COMMENT_MENTION_CANDIDATES,
        {
          OrganizationIdRef: userData.OrganizationId,
          StudyIdRef: studyId,
          StaffRecordIdRef: userData.StaffId,
        },
      );
      if (!response?.success) {
        setMentionStaff([]);
        return;
      }
      const rows =
        response?.data?.StaffListRecord ||
        response?.data?.Table ||
        (Array.isArray(response?.data) ? response.data : []);
      setMentionStaff(mapMentionCandidates(rows));
    } catch (err) {
      console.warn("comment mention candidates", err);
      setMentionStaff([]);
    }
  }, [userData?.OrganizationId, userData?.StaffId, studyId]);

  useEffect(() => {
    if (isOpen && inputNode && inputNode.DocumentIdRef && studyId) {
      fetchComments();
      loadMentionCandidates();
    } else {
      setComments([]);
      setMentionStaff([]);
      setMentionIds([]);
      setNewComment("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inputNode, studyId, loadMentionCandidates]);

  const documentMentionName =
    inputNode?.FileName ||
    inputNode?.CertificationDocumentName ||
    inputNode?.name ||
    null;

  // Fetch comments from API
  const fetchComments = async () => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
        ActionType: "Get",
        CommentId: null,
        Comment: null,
        PreviousCommentId: null,
        DocumentIdRef: inputNode?.DocumentIdRef,
        OrganizationIdRef: userData.OrganizationId,
        StaffRecordIdRef: userData.StaffId,
        StudyIdRef: studyId,
        MentionFileName: null,
        StaffIdList: null,
        IsHighAlert: false,
      });

      if (!response.success) {
        throw new Error("Failed to fetch comments");
      }

      const data = await transformCommentsData(response.data.SiteComments);

      setComments(data || data || []);
    } catch (err) {
      console.error("Error fetching comments:", err);
    } finally {
    }
  };

  // Scroll to comment when scrollToId changes
  useEffect(() => {
    if (scrollToId && commentRefs.current[scrollToId]) {
      setTimeout(() => {
        commentRefs.current[scrollToId]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
        setScrollToId(null);
      }, 100);
    }
  }, [scrollToId]);

  // Calculate comment counts
  const totalComments = comments.reduce(
    (acc, comment) => acc + 1 + (comment.replies?.length || 0),
    0
  );
  const unresolvedComments = comments
    .filter((c) => !c.resolved)
    .reduce((acc, comment) => acc + 1 + (comment.replies?.length || 0), 0);
  const resolvedComments = comments
    .filter((c) => c.resolved)
    .reduce((acc, comment) => acc + 1 + (comment.replies?.length || 0), 0);

  // Filter comments based on active tab and high priority filter
  const getFilteredComments = () => {
    let filtered = comments;

    // Filter by tab
    if (activeTab === "unresolved") {
      filtered = comments.filter((c) => !c.resolved);
    } else if (activeTab === "resolved") {
      filtered = comments.filter((c) => c.resolved);
    }

    // Filter by high priority
    if (showOnlyHigh) {
      filtered = filtered.filter((c) => c.priority === "high");
    }

    return filtered;
  };

  // Handle reply to a comment
  const handleReply = (commentId) => {
    if (!canParticipateInComments()) return;
    const comment = comments.find((c) => c.id === commentId);
    if (comment && !comment.resolved) {
      setReplyingTo(commentId);
    }
  };

  // Handle resolve comment (one-way only)
  const handleResolve = async (commentId) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
        ActionType: "Resolved",
        CommentId: commentId,
        Comment: null,
        PreviousCommentId: null,
        DocumentIdRef: inputNode?.DocumentIdRef,
        OrganizationIdRef: userData.OrganizationId,
        StaffRecordIdRef: userData.StaffId,
        StudyIdRef: studyId,
        MentionFileName: null,
        StaffIdList: null,
        IsHighAlert: false,
      });

      if (!response.success) {
        throw new Error("Failed to fetch comments");
      } else {
        setComments(
          comments.map((comment) =>
            comment.id === commentId ? { ...comment, resolved: true } : comment
          )
        );
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
    } finally {
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    const comment = comments.find((c) => c.id === commentId);
    if (!canDeleteComment(comment)) {
      showAlert({
        title: "Not allowed",
        message: "You can only delete your own comments that have no replies.",
        type: "warning",
      });
      return;
    }

    try {
      const response = await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
        ActionType: "Delete",
        CommentId: commentId,
        Comment: null,
        PreviousCommentId: null,
        DocumentIdRef: inputNode?.DocumentIdRef,
        OrganizationIdRef: userData.OrganizationId,
        StaffRecordIdRef: userData.StaffId,
        StudyIdRef: studyId,
        MentionFileName: null,
        StaffIdList: null,
        IsHighAlert: false,
      });

      if (!response.success) {
        throw new Error("Failed to fetch comments");
      }

      const target = comments.find((c) => c.id === commentId);
      if (target && !target.resolved) {
        setComments(comments.filter((item) => item.id !== commentId));
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
    }
  };

  // Handle delete reply
  const handleDeleteReply = async (commentId, replyId) => {
    const parent = comments.find((c) => c.id === commentId);
    const reply = parent?.replies?.find((r) => r.id === replyId);
    if (!canDeleteReply(reply)) {
      showAlert({
        title: "Not allowed",
        message: "You can only delete your own replies.",
        type: "warning",
      });
      return;
    }

    try {
        const response = await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
          ActionType: "Delete",
          CommentId: replyId,
          Comment: null,
          PreviousCommentId: null,
          DocumentIdRef: inputNode?.DocumentIdRef,
          OrganizationIdRef: userData.OrganizationId,
          StaffRecordIdRef: userData.StaffId,
          StudyIdRef: studyId,
          MentionFileName: null,
          StaffIdList: null,
          IsHighAlert: false,
        });

        if (!response.success) {
          throw new Error("Failed to fetch comments");
        } else {
          const comment = comments.find((c) => c.id === commentId);
          // Only allow delete if parent comment is not resolved
          if (comment && !comment.resolved) {
            setComments(
              comments.map((comment) =>
                comment.id === commentId
                  ? {
                      ...comment,
                      replies: comment.replies.filter(
                        (reply) => reply.id !== replyId
                      ),
                    }
                  : comment
              )
            );
          }
        }
      } catch (err) {
        console.error("Error fetching comments:", err);
      }
  };
  const handleChangePriority = async (
    commentId,
    isReply = false,
    parentId = null
  ) => {
    try {
      if (isReply) {
        // CHILD CLICKED: Parent + all siblings become High
        const parent = comments.find((c) => c.id === parentId);
        const childComment = parent?.replies.find((r) => r.id === commentId);

        if (!childComment || childComment.priority === "high") {
          return;
        }

        // Call API for child
        const response = await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
          ActionType: "ChangeCommentPriority",
          CommentId: commentId,
          Comment: childComment.content,
          PreviousCommentId: parentId,
          DocumentIdRef: inputNode?.DocumentIdRef,
          OrganizationIdRef: userData.OrganizationId,
          StaffRecordIdRef: userData.StaffId,
          StudyIdRef: studyId,
          MentionFileName: null,
          StaffIdList: null,
          IsHighAlert: true,
        });

        if (response.success) {
          // Update: Parent + ALL children to High
          setComments(
            comments.map((c) =>
              c.id === parentId
                ? {
                    ...c,
                    priority: "high", // Parent becomes high
                    replies: c.replies.map((r) => ({
                      ...r,
                      priority: "high", // All children become high
                    })),
                  }
                : c
            )
          );

          // Call API for parent and other siblings
          // (You can batch these or call individually)
          // await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
          //   ActionType: "ChangeCommentPriority",
          //   CommentId: parentId,
          //   Comment: parent.content,
          //   PreviousCommentId: null,
          //   DocumentIdRef: inputNode?.DocumentIdRef,
          //   OrganizationIdRef: userData.OrganizationId,
          //   StaffRecordIdRef: userData.StaffId,
          //   StudyIdRef: studyId,
          //   MentionFileName: null,
          //   StaffIdList: null,
          //   IsHighAlert: true,
          // });
        }
      } else {
        // PARENT CLICKED: Parent + all children become High
        const comment = comments.find((c) => c.id === commentId);

        if (!comment || comment.priority === "high") {
          return;
        }

        // Call API for parent
        const response = await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
          ActionType: "ChangeCommentPriority",
          CommentId: commentId,
          Comment: comment.content,
          PreviousCommentId: commentId,
          DocumentIdRef: inputNode?.DocumentIdRef,
          OrganizationIdRef: userData.OrganizationId,
          StaffRecordIdRef: userData.StaffId,
          StudyIdRef: studyId,
          MentionFileName: null,
          StaffIdList: null,
          IsHighAlert: true,
        });

        if (response.success) {
          // Update: Parent + ALL children to High
          setComments(
            comments.map((c) =>
              c.id === commentId
                ? {
                    ...c,
                    priority: "high", // Parent becomes high
                    replies: c.replies.map((r) => ({
                      ...r,
                      priority: "high", // All children become high
                    })),
                  }
                : c
            )
          );

          // Call API for all children
          // if (comment.replies && comment.replies.length > 0) {
          //   for (const reply of comment.replies) {
          //     await apiClient.post(API_ENDPOINTS.ISF.FILE_COMMENTS, {
          //       ActionType: "ChangeCommentPriority",
          //       CommentId: reply.id,
          //       Comment: reply.content,
          //       PreviousCommentId: commentId,
          //       DocumentIdRef: inputNode?.DocumentIdRef,
          //       OrganizationIdRef: userData.OrganizationId,
          //       StaffRecordIdRef: userData.StaffId,
          //       StudyIdRef: studyId,
          //       MentionFileName: null,
          //       StaffIdList: null,
          //       IsHighAlert: true,
          //     });
          //   }
          // }
        }
      }
    } catch (err) {
      console.error("Error changing priority:", err);
      alert("Failed to change priority");
    }
  };
  // Handle send comment or reply
  const handleSendComment = async () => {
    if (!canParticipateInComments()) return;
    if (!newComment.trim()) return;

    const staffIdList = buildStaffIdListFromComment(newComment, mentionOptions);

      if (replyingTo) {
        // Check if parent comment is resolved
        const parentComment = comments.find((c) => c.id === replyingTo);
        if (parentComment && parentComment.resolved) {
          // Don't allow reply to resolved comments
          setReplyingTo(null);
          return;
        }

        try {
          const response = await apiClient.post(
            API_ENDPOINTS.ISF.FILE_COMMENTS,
            {
              ActionType: "Save",
              CommentId: null,
              Comment: newComment,
              PreviousCommentId: replyingTo,
              DocumentIdRef: inputNode?.DocumentIdRef,
              OrganizationIdRef: userData.OrganizationId,
              StaffRecordIdRef: userData.StaffId,
              StudyIdRef: studyId,
              MentionFileName: documentMentionName,
              StaffIdList: staffIdList,
              IsHighAlert: markAsHigh,
              TemplateCategory: templateCategory || "",
            }
          );

          if (!response.success) {
            throw new Error("Failed to fetch comments");
          } else {
            setComments(
              comments.map((comment) =>
                comment.id === replyingTo
                  ? {
                      ...comment,
                      replies: [
                        {
                          id: response.data.SaveComments[0].Id,
                          author: response.data.SaveComments[0].StaffName,
                          authorStaffId: userData?.StaffId,
                          content: newComment,
                          timestamp:
                            response.data.SaveComments[0].SystemDateTime,
                          priority: markAsHigh ? "high" : "normal",
                        },
                        ...comment.replies,
                      ],
                    }
                  : comment
              )
            );
            setScrollToId(replyingTo);
            setReplyingTo(null);
          }
        } catch (err) {
          console.error("Error fetching comments:", err);
        } finally {
        }

        // Adding a reply

        // Scroll to parent comment
      } else {
        try {
          const response = await apiClient.post(
            API_ENDPOINTS.ISF.FILE_COMMENTS,
            {
              ActionType: "Save",
              CommentId: null,
              Comment: newComment,
              PreviousCommentId: null,
              DocumentIdRef: inputNode?.DocumentIdRef,
              OrganizationIdRef: userData.OrganizationId,
              StaffRecordIdRef: userData.StaffId,
              StudyIdRef: studyId,
              MentionFileName: documentMentionName,
              StaffIdList: staffIdList,
              IsHighAlert: markAsHigh,
              TemplateCategory: templateCategory || "",
            }
          );

          if (!response.success) {
            throw new Error("Failed to fetch comments");
          } else {
            const newCommentId = response.data.SaveComments[0].Id;
            setComments([
              {
                id: response.data.SaveComments[0].Id,
                author: response.data.SaveComments[0].StaffName,
                authorStaffId: userData?.StaffId,
                content: newComment,
                timestamp: response.data.SaveComments[0].SystemDateTime,
                priority: markAsHigh ? "high" : "normal",
                resolved: false,
                replies: [],
              },
              ...comments,
            ]);
            // Scroll to new comment
            setScrollToId(newCommentId);
          }
        } catch (err) {
          console.error("Error fetching comments:", err);
        } finally {
        }

        // Adding a new parent comment
      }

      setNewComment("");
      setMentionIds([]);
      setMarkAsHigh(false);
  };

  const transformCommentsData = (apiData) => {
    // Create a map to store all comments by their ID
    const commentsMap = new Map();

    // First pass: Create all comment objects
    apiData.forEach((item) => {
      const comment = {
        id: item.Id,
        author: item.StaffName,
        authorStaffId: item.StaffRecordIdRef || item.StaffIdRef || item.StaffId,
        content: item.Comment,
        timestamp: item.SystemDateTime,
        priority:
          item.CommentPriority.toLowerCase() === "high" ? "high" : "normal",
        resolved: item.IsResolved,
        replies: [],
        previousCommentId: item.PreviousCommentId,
      };

      commentsMap.set(item.Id, comment);
    });

    // Second pass: Build nested structure
    const rootComments = [];

    commentsMap.forEach((comment) => {
      if (comment.previousCommentId === null) {
        // This is a parent comment
        rootComments.push(comment);
      } else {
        // This is a reply - add to parent's replies array
        const parentComment = commentsMap.get(comment.previousCommentId);
        if (parentComment) {
          // Remove previousCommentId from reply
          const { previousCommentId, ...replyWithoutPreviousId } = comment;
          parentComment.replies.push(replyWithoutPreviousId);
        }
      }
    });

    // Remove previousCommentId from parent comments
    const cleanedRootComments = rootComments.map((comment) => {
      const { previousCommentId, ...cleanedComment } = comment;
      return cleanedComment;
    });

    return cleanedRootComments;
  };

  return (
    <>
      <div className="comment-system">
        {/* Header - Fixed */}
        <div className="comment-header-title">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Comments</h5>
          </div>
        </div>

        {readOnly ? (
          <div className="alert alert-light border small py-2 px-3 mb-0 mx-3 mt-2">
            Older document versions are read-only. You can view comments but
            cannot add or change them.
          </div>
        ) : null}

        {/* Tab Navigation - Fixed */}
        <div className="tab-navigation">
          <button
            className={`tab-btn ${activeTab === "unresolved" ? "active" : ""}`}
            onClick={() => setActiveTab("unresolved")}
          >
            Unresolved ({unresolvedComments})
          </button>
          <button
            className={`tab-btn ${activeTab === "resolved" ? "active" : ""}`}
            onClick={() => setActiveTab("resolved")}
          >
            Resolved ({resolvedComments})
          </button>
          <button
            className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All ({totalComments})
          </button>
        </div>

        {/* Filter Section - Fixed */}
        <div className="filter-section">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="highPriorityFilter"
              checked={showOnlyHigh}
              onChange={(e) => setShowOnlyHigh(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="highPriorityFilter">
              Show only High
            </label>
          </div>
        </div>

        {/* Comments List - Scrollable */}
        <div className="comments-list" ref={commentsListRef}>
          {getFilteredComments().map((comment) => (
            <div
              key={comment.id}
              className={`comment-wrapper ${
                comment.resolved ? "resolved" : ""
              }`}
              ref={(el) => (commentRefs.current[comment.id] = el)}
            >
              {/* Parent Comment */}
              <div className="comment-card parent-comment">
                <div className="comment-header">
                  <div className="comment-author-info">
                    {/* Show Standard or High badge */}
                    {comment.priority === "high" ? (
                      <span className="priority-badge priority-high">High</span>
                    ) : (
                      <span
                        className={`priority-badge ${
                          comment.resolved
                            ? "priority-standard-disabled"
                            : "priority-standard"
                        }`}
                        onClick={() =>
                          !readOnly &&
                          !comment.resolved &&
                          handleChangePriority(comment.id, false)
                        }
                        title={
                          comment.resolved
                            ? "Cannot change priority of resolved comment"
                            : "Click to mark as High (parent + all replies)"
                        }
                      >
                        Standard
                      </span>
                    )}
                    {comment.resolved && (
                      <span className="resolved-badge">Resolved</span>
                    )}
                    <span className="author-name">{comment.author}</span>
                    <span className="comment-timestamp">
                      {comment.timestamp}
                    </span>
                  </div>
                  <div className="comment-actions">
                    {!readOnly && !comment.resolved && (
                      <>
                        <button
                          className="action-btn reply-btn"
                          onClick={() => handleReply(comment.id)}
                          title="Reply"
                        >
                          <i className="fa fa-reply"></i>
                        </button>
                        <button
                          className="action-btn resolve-btn"
                          onClick={() => handleResolve(comment.id)}
                          title="Resolve"
                        >
                          <i className="fa fa-check-circle"></i>
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteComment(comment.id)}
                          title="Delete"
                          style={{
                            display: canDeleteComment(comment) ? undefined : "none",
                          }}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="comment-content">
                  {renderCommentBody(comment.content)}
                </div>
              </div>

              {/* Replies */}
              {comment.replies && comment.replies.length > 0 && (
                <div className="replies-container">
                  {comment.replies.map((reply) => (
                    <div key={reply.id} className="comment-card reply-comment">
                      <div className="comment-header">
                        <div className="comment-author-info">
                          {/* Show Standard or High badge */}
                          {reply.priority === "high" ? (
                            <span className="priority-badge priority-high">
                              High
                            </span>
                          ) : (
                            <span
                              className={`priority-badge ${
                                comment.resolved
                                  ? "priority-standard-disabled"
                                  : "priority-standard"
                              }`}
                              onClick={() =>
                                !readOnly &&
                                !comment.resolved &&
                                handleChangePriority(reply.id, true, comment.id)
                              }
                              title={
                                comment.resolved
                                  ? "Cannot change priority of resolved comment"
                                  : "Click to mark as High (parent + all siblings)"
                              }
                            >
                              Standard
                            </span>
                          )}
                          <span className="author-name">{reply.author}</span>
                          <span className="comment-timestamp">
                            {reply.timestamp}
                          </span>
                        </div>
                        {!readOnly && !comment.resolved && (
                          <div className="comment-actions">
                            <button
                              className="action-btn delete-btn"
                              onClick={() =>
                                handleDeleteReply(comment.id, reply.id)
                              }
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="comment-content">
                        {renderCommentBody(reply.content)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {!readOnly ? (
          <div className="input-section">
            {replyingTo && (
              <div className="replying-indicator">
                Replying to comment...
                <button
                  className="btn btn-sm btn-link"
                  onClick={() => setReplyingTo(null)}
                >
                  Cancel
                </button>
              </div>
            )}

            <div className="comment-composer">
              <div className="form-check comment-composer__priority">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="markAsHighCheck"
                  checked={markAsHigh}
                  onChange={(e) => setMarkAsHigh(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="markAsHighCheck">
                  Mark as High
                </label>
              </div>
              <MentionTextarea
                value={newComment}
                mentionIds={mentionIds}
                staffOptions={mentionOptions}
                placeholder="Type a message here"
                onChange={setNewComment}
                onMentionIdsChange={setMentionIds}
                onSubmit={handleSendComment}
              />
              <div className="comment-composer__footer">
                <p className="comment-mention-hint mb-0">
                  Type <strong>@</strong> to tag study, admin, or shared external
                  users.
                </p>
                <button
                  type="button"
                  className="btn btn-primary comment-composer__send"
                  onClick={handleSendComment}
                  title="Send comment"
                  aria-label="Send comment"
                >
                  <i className="fas fa-paper-plane" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </>
  );
};

export default CommentSystem;
